import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { MessageService } from 'primeng/api';
import { take } from 'rxjs/operators';
import { ModalService } from '@app/@core/modal/modal.service';
import { SpinnerService } from '../spinner/spinner.service';
import { CustomDialogComponent } from '../modal/customDialog/customDialog.component';
import { UserService } from '@app/services/user.service';
import { SurveyAnswerService } from '@app/surveyAnswers/surveyAnswers.service';
import { SurveyQuestion } from '@app/interface/survey';

@Component({
  selector: 'app-survey-detail-table',
  templateUrl: './surveyDetail.component.html',
  styleUrls: ['./surveyDetail.component.scss'],
})
export class SurveyDetailTableComponent implements OnChanges {
  @Input() isLoading = false;
  @Input() message: string;
  @Input() dataTable: any[] = [];
  @Input() dataColumn: string;
  @Input() surveyUUID: string;
  @Input() showAnswers: boolean = false;
  first = 0;
  rows = 5;
  values = '';
  surveyQuestionData: any[] = [];
  maxSequence: number;

  constructor(
    private modalService: ModalService,
    private messageService: MessageService,
    private spinnerService: SpinnerService,
    public userService: UserService,
    public surveyAnswerService: SurveyAnswerService
  ) {}

  getIndex(arr: any, question: any) {
    let index = arr.map((item: any) => item.id).indexOf(question['id']);
    return index;
  }

  ngOnChanges(change: SimpleChanges) {
    this.onInputChange(change);
  }

  onInputChange(change: SimpleChanges): void {
    if (change.dataTable && change.dataTable.currentValue) {
      this.dataTable = change.dataTable.currentValue;
      if (this.dataTable.length > 0) {
        let sequences = this.dataTable.map((item) => item.sequence);
        this.maxSequence = Math.max(...sequences);
      }
    } else {
      this.dataTable = [];
    }

    this.showAnswers ? this.getSurveyAnswers() : this.setSurveyData();
  }

  next() {
    this.first = this.first + this.rows;
  }

  prev() {
    this.first = this.first - this.rows;
  }

  reset() {
    this.first = 0;
  }

  isLastPage(): boolean {
    return this.dataTable ? this.first === this.dataTable.length - this.rows : true;
  }

  isFirstPage(): boolean {
    return this.dataTable ? this.first === 0 : true;
  }

  setSurveyData(): void {
    this.surveyQuestionData = this.dataTable;
  }

  getSurveyAnswers(): void {
    let userId = this.surveyAnswerService.getUserId();
    this.surveyQuestionData = this.dataTable.map((data) => {
      this.surveyAnswerService
        .getUserSurveyAnswers(data['uuid'], userId, this.surveyUUID)
        .subscribe((surveyAnswers: any) => {
          data['answer'] = surveyAnswers;
        });
      return data;
    });
  }

  deleteQuestion(question: any): void {
    this.modalService
      .confirm('Are you sure that you want to remove the following question : (' + question['qtext'] + ')')
      .pipe(take(1))
      .subscribe((confirmed: any) => {
        if (confirmed) {
          this.spinnerService.show();
          this.removeQuestion(question);
        }
      });
  }

  openEditForm(formData: any): void {
    this.modalService
      .custom(CustomDialogComponent, {
        title: 'Edit Question',
        modalType: 'add-edit-question',
        formType: 'Edit',
        formData: formData,
        dataTable: this.dataTable,
        uuidData: this.surveyUUID,
        showAnswers: this.showAnswers,
      })
      .pipe(take(1)) // take() manages unsubscription for us
      .subscribe((result: any) => {
        if (result) {
          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: 'Survey question is added succesfully.',
          });
        }
      });
  }

  removeQuestion(question: any): void {
    this.userService.deleteSurveyQuestion(question.uuid, this.surveyUUID).then(() => {
      this.spinnerService.hide();
      this.messageService.add({
        severity: 'success',
        summary: 'Success',
        detail: question['qtext'] + ' is deleted successfully',
      });
    });
  }

  public cloneQuestion(question: SurveyQuestion): void {
    question.sequence = this.maxSequence + 1;
    this.userService
      .cloneSurveyQuestion(this.surveyUUID, question)
      .then(() => {
        this.spinnerService.hide();
        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: question['qtext'] + ' is cloned successfully',
        });
      })
      .catch((error) => {
        this.spinnerService.hide();
      });
  }
}
